import ThumbImg from "../media/ThumbImg";
import { readableNameColor } from "../../lib/nameColor";
import { formatShortTime } from "../../lib/time";

// One message in a favorites card, drawn like a row in a regular chat channel (MessageListVirtualized): a round
// avatar, a coloured name with a short time, and the text on a transparent row. Consecutive messages from the same
// author within five minutes collapse under one header, as they do in the channel.

export type FavMessage = {
  id: string;
  authorId?: string;
  authorName?: string;
  authorColor?: string | null;
  createdAt?: string;
};

const GROUP_WINDOW_MS = 5 * 60 * 1000;

/** True when `msg` continues `prev`'s block: same author, no day change, under five minutes apart. */
export function continuesGroup(msg: FavMessage, prev?: FavMessage | null): boolean {
  if (!prev || !msg.createdAt || !prev.createdAt) return false;
  const sameAuthor = msg.authorId ? msg.authorId === prev.authorId : !!msg.authorName && msg.authorName === prev.authorName;
  if (!sameAuthor) return false;
  const a = new Date(msg.createdAt);
  const b = new Date(prev.createdAt);
  return a.toDateString() === b.toDateString() && a.getTime() - b.getTime() < GROUP_WINDOW_MS;
}

type Props = {
  msg: FavMessage;
  grouped: boolean;
  isMine: boolean;
  avatarUrl?: string | null;
  /** The message text (already linkified) or a short stand-in such as "Shared an image". */
  children: React.ReactNode;
};

export default function FavMessageRow({ msg, grouped, isMine, avatarUrl, children }: Props) {
  const label = isMine ? "You" : msg.authorName || "User";
  const color = readableNameColor(msg.authorColor || undefined);
  return (
    <div className={`flex items-start gap-3 rounded-lg px-1 transition-colors hover:bg-white/[0.03] ${grouped ? "py-0.5" : "pt-2 pb-0.5"}`}>
      {grouped ? (
        <div className="w-9 shrink-0" />
      ) : (
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/10">
          {avatarUrl ? (
            <ThumbImg w={56} src={avatarUrl} alt={label} className="h-full w-full object-cover" />
          ) : (
            <span className="text-xs font-medium text-fg-2">{(label[0] || "?").toUpperCase()}</span>
          )}
        </div>
      )}
      <div className="min-w-0 flex-1">
        {!grouped && (
          <div className="mb-0.5 flex items-baseline gap-2">
            <span className="truncate text-sm font-medium leading-tight" style={color ? { color } : undefined}>{label}</span>
            <span className="shrink-0 text-meta text-fg-3">{formatShortTime(msg.createdAt)}</span>
          </div>
        )}
        <div className="min-w-0 whitespace-pre-wrap break-words text-sm text-fg [overflow-wrap:anywhere]">{children}</div>
      </div>
    </div>
  );
}
